// One of the stacked panels in the right-hand column.
//
// The column is a set of views onto the same run — the lock plan, the trace, the
// transaction graph, the tree of timelines — and which of them you want open
// depends entirely on what you are doing. So each is a header you can click to
// fold it away, and an open panel shares what the folded ones gave up.
//
// A header is a title and a fold, and nothing else. Controls that belong to the
// panel — the lock plan's strategy switch, the compiler's backend tabs, the
// timeline tree's reset — live at the top of its *contents*, where they read as
// part of the thing rather than as chrome, and where clicking one can't be
// mistaken for asking to fold the panel away.

interface PanelProps {
  id: string;
  title: string;
  /** Extra weight when open, so the panel that most wants the room gets it.
   *  Zero asks for the opposite: as tall as its contents and no taller, for a
   *  panel holding a short list (the console's handful of printed lines) rather
   *  than a view that fills whatever it is handed. It takes what it needs — up
   *  to a cap in CSS, past which it scrolls — and leaves the rest to the panels
   *  that can use it. */
  grow?: number;
  collapsed: boolean;
  onToggle(): void;
  children: React.ReactNode;
}

export function Panel({ id, title, grow = 1, collapsed, onToggle, children }: PanelProps) {
  return (
    <section
      id={id}
      className={`rpanel${collapsed ? " collapsed" : ""}${grow === 0 ? " fit" : ""}`}
      style={
        collapsed ? undefined : grow === 0 ? { flexGrow: 0, flexBasis: "auto" } : { flexGrow: grow }
      }
    >
      <header
        className="rpanel-head"
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <span className={`rpanel-twisty${collapsed ? "" : " open"}`}>›</span>
        <span className="rpanel-title">{title}</span>
      </header>
      {!collapsed && <div className="rpanel-body">{children}</div>}
    </section>
  );
}
